// @flow

import React, { Component } from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { provideHooks } from 'redial';
import { renderRoutes } from 'react-router-config';
import {
  StyleGuideProvider,
  ScreenReaderSkipLink,
  Header,
  Footer
} from 'seek-style-guide/react';

import { isCookieSet } from 'src/modules/app-install-banner';

import { fetchSavedSearches } from 'src/store/saveSearch/saveSearch';
import { getCandidateAccount, updateSession } from 'src/store/user/user';
import { getMetaDescription, setCanonicalUrl } from 'src/store/seo/seo';
import { getNudges } from 'src/store/nudges/nudges';
import { updateFeatureFlags } from 'src/store/featureFlags/featureFlags';
import {
  trackBannerEvent,
  trackBannerImpression
} from 'src/store/appInstallBanner/appInstallBanner';
import { SafetyHelmet } from 'src/components/SafetyHelmet/SafetyHelmet';
import HeaderFooterLink from 'src/components/HeaderFooterLink/HeaderFooterLink';
import ContentSpacer from 'src/components/ContentSpacer/ContentSpacer';
import AppInstallBanner from 'src/modules/app-install-banner';

import {
  jobDetailsPageRegex,
  searchResultsPageRegex,
  expiredJobPageRegex
} from 'src/modules/routes-regexp';
import Experiments, {
  Provider as ExperimentsProvider
} from '@seek/experiments-js';
import experimentsHttpClient from 'src/modules/experiments-helpers';
import {
  experimentsEnabled,
  experimentsPublicEndpoint,
  experimentsPrivateEndpoint,
  activeExperiments
} from 'src/config';
import {
  setActiveExperiments,
  formatExperiments
} from 'src/modules/experiments-helpers';
import { metrics } from '@seek/metrics-js';
import { isGooglebot } from 'src/modules/chalice-user-agent/googlebot';
import { getUserAgent } from 'src/modules/chalice-user-agent/isomorphic-useragent';

// AppInstallBanner Themes
import FullPageTheme from 'src/modules/app-install-banner-themes/fullpage';
import { getSharedHead } from '@seek/seek-jobs-seo';

setActiveExperiments(activeExperiments);

const experiment = new Experiments();

const metricsReduction = (reduction, { name, variation }) => [
  ...reduction,
  `experiment:${name}`,
  `experiment:${name}:variation:${variation.index}`
];

const hooks = {
  first({ cookies, dispatch, getState, routeEnter, requestId }) {
    const { JobseekerVisitorId } = cookies;

    return experiment
      .initialise({
        dispatch,
        enabled: experimentsEnabled && !isGooglebot(getUserAgent()),
        host: experimentsPublicEndpoint,
        privateHost: experimentsPrivateEndpoint,
        http: experimentsHttpClient(requestId),
        initialState: (ENV.CLIENT && getState().experiments) || false,
        server: ENV.SERVER,
        userId: JobseekerVisitorId
      })
      .then(experiments => {
        if (routeEnter) {
          metrics.addGlobalTags(
            formatExperiments(metricsReduction, experiments)
          );
        }

        dispatch(updateFeatureFlags(cookies, experiments));
      });
  },

  seo({ dispatch, locale, path, query }) {
    return Promise.all([
      dispatch(getMetaDescription({ locale, path })),
      dispatch(setCanonicalUrl({ locale, path, query }))
    ]);
  },

  defer({ routeEnter, dispatch, cookies }) {
    if (routeEnter) {
      return dispatch(
        updateSession({
          userClientId: cookies.JobseekerVisitorId,
          sessionId: cookies.JobseekerSessionId
        })
      );
    }
    return null;
  },

  authenticated: ({ dispatch, locale, routeEnter }) => {
    if (routeEnter) {
      return dispatch(getCandidateAccount()).then(() => {
        dispatch(getNudges({ locale }));

        dispatch(fetchSavedSearches(locale));
      });
    }

    return null;
  }
};

function mapStateToProps({
  seo,
  location,
  user,
  jobdetails,
  featureFlags,
  results
}) {
  return {
    isZeroResults: get(results, 'results.isZeroResults', false),
    pageLoadedCount: jobdetails.pageLoadedCount,
    authenticated: user.authenticated,
    userName: user.firstName,
    emailAddress: user.emailAddress,
    query: location.query,
    locale: location.locale,
    metaDescription: seo.metaDescription,
    pageTitle: location.pageTitle,
    pathname: location.pathname,
    prevPathname: location.prevPathname,
    returnUrl: location.url,
    canonicalUrl: seo.canonicalUrl,
    appInstallNative: featureFlags.appInstallNative
  };
}

const mapDispatchToProps = {
  trackBannerEvent,
  trackBannerImpression
};

type Props = {
  appInstallNative: boolean,
  authenticated: boolean,
  canonicalUrl?: string,
  emailAddress: string,
  isZeroResults: boolean,
  locale: Locale,
  metaDescription?: string,
  pageLoadedCount: number,
  pageTitle?: string,
  pathname: string,
  prevPathname?: string,
  query?: SearchParams,
  returnUrl?: string,
  route: any,
  trackBannerEvent: Function,
  trackBannerImpression: Function,
  userName: string
};

type State = {|
  shouldShowFullInterstitial?: boolean
|};

class App extends Component<Props, State> {
  static defaultProps = {
    pageTitle: ''
  };

  state = {
    shouldShowFullInterstitial: false
  };

  componentWillReceiveProps(nextProps) {
    const NUMBER_OF_JOB_VIEWS_BEFORE_SHOWING_APP_BANNER = 1;
    const { pageLoadedCount, pathname, prevPathname } = nextProps;
    const isNavigatedFromSerp = searchResultsPageRegex.test(prevPathname);
    const isJobDetails = jobDetailsPageRegex.test(pathname);
    const isPageViewedNthTime =
      pageLoadedCount > 0 &&
      pageLoadedCount % NUMBER_OF_JOB_VIEWS_BEFORE_SHOWING_APP_BANNER === 0;

    const shouldShowFullInterstitial =
      isNavigatedFromSerp &&
      !isCookieSet() &&
      isPageViewedNthTime &&
      isJobDetails;

    if (shouldShowFullInterstitial !== this.state.shouldShowFullInterstitial) {
      this.setState({ shouldShowFullInterstitial });
    }
  }

  render() {
    const {
      authenticated,
      trackBannerImpression: _trackBannerImpression,
      trackBannerEvent: _trackBannerEvent,
      canonicalUrl,
      emailAddress,
      locale,
      metaDescription,
      pageTitle,
      pathname,
      query,
      returnUrl,
      userName,
      route,
      appInstallNative,
      isZeroResults
    } = this.props;

    const { meta, link, script, title } = getSharedHead({
      pageTitle,
      canonicalUrl,
      metaDescription,
      query,
      locale,
      isZeroResults,
      appInstallNative,
      pathname
    });

    const isAuthenticatedOrPending =
      userName || emailAddress ? 'authenticated' : 'pending';
    const authState = authenticated
      ? isAuthenticatedOrPending
      : 'unauthenticated';

    return (
      <ExperimentsProvider experiments={experiment}>
        <StyleGuideProvider title={title} locale={locale}>
          <div>
            <SafetyHelmet meta={meta} link={link} script={script} />
            <ScreenReaderSkipLink to="start-of-content">
              Skip to content
            </ScreenReaderSkipLink>
            <AppInstallBanner
              campaignSuffix={appInstallNative ? 'has-native' : 'no-native'}
              forceActive={this.state.shouldShowFullInterstitial}
              position="full-page"
              transition="fade"
              locale={locale}
              pathname={pathname}
              showOnPaths={[{ id: 'jdp', regex: jobDetailsPageRegex }]}
              targetDevices={['iPhone', 'Android']}
              theme={FullPageTheme}
              trackingFunctions={{
                standardTracking: _trackBannerEvent,
                impression: _trackBannerImpression
              }}
            />

            <Header
              locale={locale}
              userName={userName}
              userEmail={emailAddress}
              authenticationStatus={authState}
              linkRenderer={HeaderFooterLink}
              divider={
                jobDetailsPageRegex.test(pathname) ||
                expiredJobPageRegex.test(pathname)
              }
              activeTab="Job Search"
              returnUrl={returnUrl}
            />

            <div role="main">{renderRoutes(route.routes)}</div>

            <ContentSpacer
              isSlider
              trigger={['dashboard', 'searchbar', 'refinebar']}
            >
              <Footer
                locale={locale}
                linkRenderer={HeaderFooterLink}
                authenticationStatus={authState}
              />
            </ContentSpacer>

            <ContentSpacer />
          </div>
        </StyleGuideProvider>
      </ExperimentsProvider>
    );
  }
}

export default provideHooks(hooks)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(App)
);
